import {
  DrugUnitsEnum,
  INFUSION_TYPES_VALUES,
  INFUSION_TYPES_VALUES_NO_INTERMITTENT,
} from 'constants/drugs';
import { Concentration, ConcentrationFormData } from 'interfaces/concentration';
import { FC, useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

/**
 * @description Props for the InfusionType component
 * @interface InfusionTypeProps
 */
type InfusionTypeProps = {
  /** The concentration record being edited */
  record: Concentration;
  /** React Hook Form control for the concentration form */
  control: Control<ConcentrationFormData, any>;
  /** Whether the form is in confirmed/read-only state */
  isConfirmed: boolean;
  /** The currently selected infusion type value */
  watchInfusionType:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Whether the user has permission to manage/edit the form */
  canManage: boolean;
};

/**
 * @description Component for selecting the infusion type in the concentration form.
 * Displays select dropdown or read-only value, filters options based on drug unit.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {InfusionTypeProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const InfusionType: FC<InfusionTypeProps> = ({
  record,
  control,
  isConfirmed,
  watchInfusionType,
  canManage,
}) => {
  const { t } = useTranslation();

  /**
   * @description Determines which infusion type options to display based on the drug unit
   * Excludes intermittent option for certain drug units
   */
  const infusionTypeValues = useMemo(() => {
    if (
      record.drug_unit === DrugUnitsEnum.NANOGRAM ||
      record.drug_unit === DrugUnitsEnum.MILLIUNIT
    ) {
      return { ...INFUSION_TYPES_VALUES_NO_INTERMITTENT };
    }

    return INFUSION_TYPES_VALUES;
  }, [record]);

  return (
    <div className={`row mt-2 position-relative`}>
      {!canManage && (
        <div className="device-concentration--disabled-item"></div>
      )}
      <div className="device-concentration-form__label">
        {t('device.infusion_type')}
      </div>

      {isConfirmed ? (
        <div className="device-concentration-form__column device-concentration-form__concentration--infusion-type">
          {watchInfusionType?.label}
        </div>
      ) : (
        <div className="device-concentration-form__select">
          <Controller
            control={control}
            name="infusion_type"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <Select
                id="infusionType"
                aria-label="infusion_type"
                maxMenuHeight={300}
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                value={value as any}
                isMulti={false}
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                isSearchable
                placeholder="- Select type -"
                options={Object.keys(infusionTypeValues).map((key: string) => ({
                  label:
                    infusionTypeValues[key as keyof typeof infusionTypeValues],
                  value: key,
                }))}
                isDisabled={!canManage}
              />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default InfusionType;
